import { useCallback, useMemo } from 'react';
import { ROW_HEIGHT } from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import { MAX_ISSUES_PER_PAGE } from '../../common/constants.tsx';
import { useInfiniteScrollProps } from '../issue-search/selectors.tsx';

const INFINITE_SCROLL_DEFAULT_OPTIONS = {
	pageSize: MAX_ISSUES_PER_PAGE,
	threshold: 5 * ROW_HEIGHT,
};

interface InfiniteScrollProps {
	onLoadNext: () => void;
	onLoadPrevious: () => void;
	isLoadingNext: boolean;
	isLoadingPrevious: boolean;
	hasNext: boolean;
	hasPrevious: boolean;
	activationThreshold: number;
}

type Options = {
	pageSize?: number;
	threshold?: number;
};

/**
 * A hook that makes it easier to consume infinite scroll props.
 *
 * @param fieldSetIds Field set IDs currently displayed for issue results.
 * @param options.pageSize Determines the page size to be returned from the backend. Defaults to `50`.
 * @param options.threshold How far, in pixels, from the bottom or top of the issue list the next/previous page of items should start loading. Defaults to `200`.
 * @returns A result object with a stable prop getter `getInfiniteScrollProps`
 */
export function useInfiniteScroll({
	fieldSetIds,
	groupBy,
	options,
}: {
	fieldSetIds: string[];
	groupBy?: string | null;
	options?: Options;
}): InfiniteScrollProps {
	const { pageSize, threshold } = { ...INFINITE_SCROLL_DEFAULT_OPTIONS, ...options };
	const {
		onLoadNext: onLoadNextProp,
		onLoadPrevious: onLoadPreviousProp,
		...rest
	} = useInfiniteScrollProps();

	// Extra static variables to ensure consistent field set results are returned during pagination.
	const staticPaginationVariables = useMemo(
		() => ({
			fieldSetsInput: {
				fieldSetIds,
			},
			groupBy,
		}),
		[fieldSetIds, groupBy],
	);

	const onLoadNext = useCallback(
		() =>
			onLoadNextProp(pageSize, {
				UNSTABLE_extraVariables: staticPaginationVariables,
			}),
		[onLoadNextProp, pageSize, staticPaginationVariables],
	);
	const onLoadPrevious = useCallback(
		() =>
			onLoadPreviousProp(pageSize, {
				UNSTABLE_extraVariables: staticPaginationVariables,
			}),
		[onLoadPreviousProp, pageSize, staticPaginationVariables],
	);

	const infiniteScrollProps = useMemo<InfiniteScrollProps>(
		() => ({
			onLoadNext,
			onLoadPrevious,
			activationThreshold: threshold,
			...rest,
		}),
		[onLoadNext, onLoadPrevious, threshold, rest],
	);

	return infiniteScrollProps;
}
